// src/ui/steps/step1-rol.jsx — omschrijf opdracht + AI rol-suggestie
import React from "react";
import { rankItem } from "@tanstack/match-sorter-utils";
import {
  FW, FwButton, FwTextarea, FwInput, FwChip, FwInlineBanner,
  StepHeader, NiveauBadge, AsideCard, RolTooltip,
} from "../primitives";
import { aiHelp } from "../../lib/ai";
import { config } from "../../lib/config";
import { buildFestivalFamilies, stripVariantLetter } from "../../lib/festival-families";

export const StepRol = ({ state, setState, next, back }) => {
  const huidige = state.rollen?.[0];
  const [omschrijving, setOmschrijving] = React.useState(state.omschrijving || "");
  React.useEffect(() => {
    setState(s => s.omschrijving === omschrijving ? s : { ...s, omschrijving });
  }, [omschrijving]);
  const [suggesties, setSuggesties] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [aiError, setAiError] = React.useState(false);
  const [selectedRol, setSelectedRol] = React.useState(huidige?.rol || null);
  const [selectedDep, setSelectedDep] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  const isFestival = state.type === "festival";
  // Festival: dedupliceer variant-rijen tot families (variant A t/m E volgt in
  // step2 via de variant-keuze of criteria-modal). Film/AV blijft 1 rij per rol.
  const rollen = React.useMemo(
    () => isFestival ? buildFestivalFamilies(config.rollen_festival) : config.rollen_film_av,
    [isFestival],
  );

  const aiCtrlRef = React.useRef(null);
  React.useEffect(() => () => aiCtrlRef.current?.abort(), []);

  const vraagAI = async () => {
    if (!omschrijving.trim() || omschrijving.trim().length < 10) return;
    aiCtrlRef.current?.abort();
    const ctrl = new AbortController();
    aiCtrlRef.current = ctrl;
    setLoading(true);
    setSuggesties(null);
    setAiError(false);
    const res = await aiHelp.suggestRol(omschrijving, isFestival, { signal: ctrl.signal });
    if (ctrl.signal.aborted) return;
    setLoading(false);
    if (res) setSuggesties(res);
    else setAiError(true);
  };

  const verderMet = (rolObj) => {
    // Festival: vind de familie-record (rolObj kan een AI-suggestie zijn die
    // toch een variant-letter teruggeeft — strip die voor lookup).
    let familieRec = null;
    if (isFestival) {
      const familieNaam = stripVariantLetter(rolObj.rol);
      familieRec = rollen.find(f => f.rol === familieNaam && f.dep === rolObj.dep)
                || rollen.find(f => f.rol === familieNaam);
    }

    const bestaande = state.rollen?.[0];
    const rolNaamNw = familieRec?.rol ?? rolObj.rol;
    const wisseltRol = bestaande && bestaande.rol !== rolNaamNw;
    const nieuwId = window.crypto?.randomUUID?.() || `r-${Date.now()}`;

    // Niveau bepalen:
    //  - festival, familie met varianten: null (wordt in step2 via variant-keuze geset)
    //  - festival, familie zonder varianten: vaste schaal uit variant_schalen._
    //  - film/AV: bestaande logica (Excel `niveaus[]` of AI `niveau`)
    let niveausLijst, gekozenNiveau;
    if (isFestival && familieRec) {
      niveausLijst = familieRec.niveaus;
      gekozenNiveau = familieRec.heeftVarianten
        ? null
        : familieRec.variant_schalen._;
    } else {
      niveausLijst = Array.isArray(rolObj.niveaus) && rolObj.niveaus.length
        ? rolObj.niveaus
        : (rolObj.niveau ? [rolObj.niveau] : []);
      gekozenNiveau = rolObj.niveau && niveausLijst.includes(rolObj.niveau)
        ? rolObj.niveau
        : niveausLijst[0];
    }

    const nieuweRol = {
      id: wisseltRol ? nieuwId : (bestaande?.id || nieuwId),
      rol: rolNaamNw,
      dep: rolObj.dep,
      niveaus: niveausLijst,
      niveau: gekozenNiveau,
      niveau_bron: "rol-lookup",
      ...(isFestival && familieRec ? {
        familie: familieRec.rol,
        variant_schalen: familieRec.variant_schalen,
        heeftVarianten: familieRec.heeftVarianten,
        festivaltype: null,
      } : {}),
      uren: wisseltRol
        ? { hoofdwerk: 0, voorbereiding: 0, afronding: 0, pr_promotie: 0 }
        : (bestaande?.uren || { hoofdwerk: 0, voorbereiding: 0, afronding: 0, pr_promotie: 0 }),
      urenDetails: wisseltRol ? undefined : bestaande?.urenDetails,
      onderhandelsignalen: wisseltRol ? [] : (bestaande?.onderhandelsignalen || []),
      // Rol-wisseling invalideert alle downstream rol-specifieke antwoorden
      // (criteria-vragen 3.1, werkdagen 4, loondienst-blokken).
      criteriaAntwoorden: wisseltRol ? {} : (bestaande?.criteriaAntwoorden || {}),
      werkdagen:    wisseltRol ? undefined : bestaande?.werkdagen,
      dienstverband: wisseltRol ? undefined : bestaande?.dienstverband,
      periodiek:    wisseltRol ? undefined : bestaande?.periodiek,
    };
    setState(s => ({
      ...s,
      omschrijving,
      rollen: [nieuweRol],
    }));
    next();
  };

  const departementen = [...new Set(rollen.map(r => r.dep))];
  const omschrijvingVoor = (dep, rolNaam) =>
    rollen.find(x => x.dep === dep && x.rol === rolNaam)?.omschrijving || "";

  // Ranking via @tanstack/match-sorter-utils: rol > alias > dep > omschrijving.
  // Multi-token query: alle tokens moeten in minstens één veld matchen; per veld
  // sommeren we de ranks zodat een rol die op meerdere tokens scoort hoger uitkomt.
  // Aliases (Synoniemenlijst, bron B / bron 6) — als een match via alias komt, tonen
  // we welke alias het was zodat de gebruiker context heeft.
  const rankOpts = { keepDiacritics: false };
  const gefilterdeRollen = React.useMemo(() => {
    const q = searchQuery.trim();
    const baseFilter = (r) => !selectedDep || r.dep === selectedDep;
    if (!q) return rollen.filter(baseFilter).map(r => ({ rol: r, matchedAlias: null }));
    const tokens = q.split(/\s+/).filter(Boolean);

    return rollen
      .filter(baseFilter)
      .map(r => {
        const oms = r.omschrijving || "";
        const aliasArr = Array.isArray(r.aliases) ? r.aliases : [];
        // Per token: rank op rol/dep/oms + best-rankende alias (incl. index)
        const perToken = tokens.map(t => {
          let bestAlias = { rank: 0, passed: false };
          let bestAliasIdx = -1;
          for (let i = 0; i < aliasArr.length; i++) {
            const ar = rankItem(aliasArr[i], t, rankOpts);
            if (ar.passed && ar.rank > bestAlias.rank) {
              bestAlias = ar;
              bestAliasIdx = i;
            }
          }
          return {
            rol: rankItem(r.rol, t, rankOpts),
            alias: bestAlias,
            aliasIdx: bestAliasIdx,
            dep: rankItem(r.dep, t, rankOpts),
            oms: rankItem(oms, t, rankOpts),
          };
        });
        const allPass = perToken.every(p => p.rol.passed || p.alias.passed || p.dep.passed || p.oms.passed);
        const sumRol = perToken.reduce((s, p) => s + p.rol.rank, 0);
        const sumAlias = perToken.reduce((s, p) => s + p.alias.rank, 0);
        const sumDep = perToken.reduce((s, p) => s + p.dep.rank, 0);
        const sumOms = perToken.reduce((s, p) => s + p.oms.rank, 0);
        // Toon alias-indicator alleen als alias-match sterker is dan rol-naam-match
        // (bij gelijke score kiest de gebruiker zelf; dan is rol-naam zelf-evident).
        let matchedAlias = null;
        if (sumAlias > sumRol) {
          const score = new Map();
          for (const p of perToken) {
            if (p.aliasIdx >= 0) {
              score.set(p.aliasIdx, (score.get(p.aliasIdx) || 0) + p.alias.rank);
            }
          }
          let bestIdx = -1, bestScore = 0;
          for (const [idx, s] of score) {
            if (s > bestScore) { bestScore = s; bestIdx = idx; }
          }
          if (bestIdx >= 0) matchedAlias = aliasArr[bestIdx];
        }
        return { r, allPass, sumRol, sumAlias, sumDep, sumOms, matchedAlias };
      })
      .filter(x => x.allPass)
      .sort((a, b) =>
        b.sumRol - a.sumRol ||
        b.sumAlias - a.sumAlias ||
        b.sumDep - a.sumDep ||
        b.sumOms - a.sumOms,
      )
      .map(({ r, matchedAlias }) => ({ rol: r, matchedAlias }));
  }, [rollen, selectedDep, searchQuery]);

  return (
    <div>
      <StepHeader
        tag={isFestival ? "Filmfestival" : "Filmproductie"}
        title="Vertel ons over je opdracht"
        sub="We gebruiken deze omschrijving om eerst je rol voor te stellen. Daarna volgt het voorstel voor het bijbehorende zwaarteniveau."
        onBack={back}
      />

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <FwTextarea
            label="Omschrijving van de opdracht"
            placeholder={isFestival
              ? "Bijv: Ik coördineer de programmering voor een internationaal documentaire-festival in Amsterdam, 4 festivaldagen, ongeveer 40 films."
              : "Bijv: Ik ben 1st AC op een Nederlandse speelfilm, 32 draaidagen, veel nachtopnames in Rotterdam, ervaren team."}
            rows={6}
            value={omschrijving}
            onChange={e => setOmschrijving(e.target.value)}
            hint={`${omschrijving.length} tekens — meer detail = betere suggestie voor een rol`}
          />
          <div style={{ marginTop: 16, display: "flex", gap: 12, alignItems: "center" }}>
            <FwButton variant="primary" icon="ri-sparkling-2-line" arrow={false}
              onClick={vraagAI} disabled={loading || omschrijving.trim().length < 10}>
              {loading ? "Even denken…" : "Stel een rol voor"}
            </FwButton>
            <span style={{ fontSize: 14, color: FW.gray500 }}>
              of zoek direct in de rollenlijst hieronder ↓
            </span>
          </div>

          {aiError && (
            <FwInlineBanner type="warning" onDismiss={() => setAiError(false)} style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Suggestie mislukt — kies je rol via de lijst hieronder.</div>
              {window.__lastAiError && (
                <div style={{ fontSize: 13, color: FW.navy2, fontFamily: "ui-monospace, monospace" }}>
                  {window.__lastAiError}
                </div>
              )}
            </FwInlineBanner>
          )}

          {suggesties && (
            <div style={{ marginTop: 24, padding: 20, background: "#FEF9B3", borderRadius: 16, border: `1.5px solid ${FW.yellow}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: FW.navy }}>
                <i className="ri-sparkling-2-line" /> Voorstel op basis van je omschrijving
              </div>
              <div style={{ fontSize: 15, color: FW.navy2, marginBottom: 16, lineHeight: 1.45 }}>{suggesties.toelichting}</div>
              <div style={{ display: "grid", gap: 10 }}>
                {suggesties.top_rollen?.map((r, i) => {
                  // Voor festival is `rollen` een families-array; AI mag óf de
                  // familienaam óf (legacy) een variant-naam teruggeven — strip
                  // de letter voor de lookup. Voor film/AV: directe match.
                  const lookupNaam = isFestival ? stripVariantLetter(r.rol) : r.rol;
                  const cfg = rollen.find(x => x.dep === r.dep && x.rol === lookupNaam);
                  const niveausVoorRol = cfg?.niveaus || (r.niveau ? [r.niveau] : []);
                  const toonNaam = cfg?.rol || lookupNaam;
                  return (
                    <button key={i} onClick={() => verderMet({ ...r, rol: toonNaam, niveaus: niveausVoorRol })} style={rolCard(i === 0)}>
                      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 18, fontWeight: 700, color: FW.navy }}>
                          {toonNaam}
                          <RolTooltip tekst={omschrijvingVoor(r.dep, toonNaam)} />
                        </div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          {isFestival
                            ? niveausVoorRol.map(n => <NiveauBadge key={n} niveau={n} />)
                            : <NiveauBadge niveau={r.niveau} />}
                          <span style={{ fontSize: 14, color: FW.gray500 }}>{r.dep}</span>
                        </div>
                      </div>
                      <div style={{ fontSize: 14, color: FW.navy2, marginTop: 4 }}>{r.reden}</div>
                      <div style={{ marginTop: 10, fontSize: 14, fontWeight: 700, color: FW.navy, display: "inline-flex", alignItems: "center", gap: 6 }}>
                        Kies deze rol <span style={{ display: "inline-grid", placeItems: "center", width: 22, height: 22, borderRadius: 999, background: FW.navy, color: "#fff", fontSize: 12 }}>→</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginTop: 24, padding: 20, background: FW.cream, borderRadius: 16, border: `1.5px solid ${FW.gray100}` }}>
            <FwInput placeholder="Zoek op rol of departement..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                containerStyle={{ marginBottom: 12 }} />
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                <FwChip active={!selectedDep} onClick={() => setSelectedDep(null)}>Alle</FwChip>
                {departementen.map(d => (
                  <FwChip key={d} active={selectedDep === d} onClick={() => setSelectedDep(selectedDep === d ? null : d)}>{d}</FwChip>
                ))}
              </div>
              <div style={{ maxHeight: 300, overflowY: "auto", display: "grid", gap: 6 }}>
                {gefilterdeRollen.map(({ rol: r, matchedAlias }, i) => {
                  const niveausLijst = r.niveaus || (r.niveau ? [r.niveau] : []);
                  return (
                    <button key={i} onClick={() => verderMet(r)} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "10px 14px", background: FW.white, border: `1px solid ${FW.gray100}`,
                      borderRadius: 10, cursor: "pointer", fontFamily: "var(--font-sans)",
                      textAlign: "left",
                    }}>
                      <div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 15, fontWeight: 700, color: FW.navy }}>
                          {r.rol}
                          <RolTooltip tekst={r.omschrijving} />
                        </div>
                        <div style={{ fontSize: 13, color: FW.gray500 }}>{r.dep}</div>
                        {matchedAlias && (
                          <div style={{ fontSize: 12, color: FW.gray500, fontStyle: "italic", marginTop: 2 }}>
                            <i className="ri-arrow-right-line" style={{ marginRight: 4 }} />
                            ook bekend als: {matchedAlias}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {niveausLijst.map(n => <NiveauBadge key={n} niveau={n} />)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-lightbulb-line" /> Tip
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.5, color: FW.navy2 }}>
            Noem vooral <b>de taken en werkzaamheden van jouw rol</b>, en daarnaast: het <b>type audiovisuele productie</b>, of het een <b>grote en/of complexe productie</b> is, het <b>afbreukrisico</b> (financieel/reputatie), het <b>budget</b>, de <b>wijze van financiering</b> (nationaal/internationaal), <b>locaties</b> (binnen/buiten), <b>draaidagen</b> (aantal en verschillende (internationale) locaties) en de <b>omvang van het team</b>.
          </div>
          <div style={{ marginTop: 16, padding: 14, background: FW.cream, borderRadius: 12, fontSize: 13, color: FW.navy2, lineHeight: 1.5 }}>
            <b>Voorbeeld:</b><br/>
            "Focus Puller: Past de scherpte van de lens aan ten behoeve van het shot, beheert en onderhoudt de camera-apparatuur. Maakt beslissingen op basis van specialistische kennis en vaardigheden die consequenties hebben voor beeldopnamen. Selecteert en stuurt het verdere camerateam aan en heeft contact met andere departementen die meestal adviserend en/of beïnvloedend van aard zijn."
          </div>
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <FwButton variant="ghost" arrow={false} onClick={back}>← Terug</FwButton>
      </div>
    </div>
  );
};

const rolCard = (primary) => ({
  background: FW.white, border: `1.5px solid ${primary ? FW.navy : FW.gray100}`,
  borderRadius: 14, padding: 16, textAlign: "left", cursor: "pointer",
  fontFamily: "var(--font-sans)", width: "100%",
  boxShadow: primary ? "0 4px 12px rgba(0,0,0,.08)" : "none",
});
